import React, { Suspense } from 'react'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom'

// Static imports for critical auth components
import { AuthProvider } from './auth/AuthProvider'
import { AuthGuard } from './auth/AuthGuard'
import { CanvasGuard } from './guards/CanvasGuard'
import { GoogleAnalytics } from './seo/Analytics'
import { ScrollToTop } from './ScrollToTop'
import LazyChunkErrorBoundary from './errors/LazyChunkErrorBoundary'
import { ContactSupportPage } from '../pages/ContactSupportPage'
import { DraftsPage } from '../pages/DraftsPage'
import { NewBoardTemplatePage } from '../pages/NewBoardTemplatePage'
import { OrdersPage } from '../pages/PrintsPage'
import AccountPage from '../pages/AccountPage'
import { CalmShellPlaceholder } from './ui/loading'
import { useMobileDetection } from '../hooks/useMobileDetection'
import { MobileBoardsPage } from './mobile/MobileBoardsPage'

// Lazy-loaded components for better code splitting
const LandingPage = React.lazy(() =>
  import('../pages/LandingPage').then((module) => ({
    default: module.LandingPage,
  }))
)
const ContactPage = React.lazy(() =>
  import('../pages/ContactPage').then((module) => ({
    default: module.ContactPage,
  }))
)
const TermsPage = React.lazy(() =>
  import('../pages/TermsPage').then((module) => ({ default: module.TermsPage }))
)
const PrivacyPage = React.lazy(() =>
  import('../pages/PrivacyPage').then((module) => ({
    default: module.PrivacyPage,
  }))
)
const ReturnPolicyPage = React.lazy(() =>
  import('../pages/ReturnPolicyPage').then((module) => ({
    default: module.ReturnPolicyPage,
  }))
)
const AboutPage = React.lazy(() =>
  import('../pages/AboutPage').then((module) => ({
    default: module.AboutPage,
  }))
)
const SitemapPage = React.lazy(() =>
  import('../pages/SitemapPage').then((module) => ({
    default: module.SitemapPage,
  }))
)

// Admin pages
const DesignSystemPage = React.lazy(() =>
  import('../pages/DesignSystemPage').then((module) => ({
    default: module.DesignSystemPage,
  }))
)

// Protected pages - lazy loaded
const BoardsPage = React.lazy(() =>
  import('../pages/BoardsPage').then((module) => ({
    default: module.BoardsPage,
  }))
)
const CanvasApp = React.lazy(() =>
  import('../CanvasApp').then((module) => ({
    default: module.default,
  }))
)
const LoginPage = React.lazy(() =>
  import('../pages/LoginPage').then((module) => ({ default: module.LoginPage }))
)
const ResetPasswordPage = React.lazy(() =>
  import('../pages/ResetPasswordPage').then((module) => ({
    default: module.ResetPasswordPage,
  }))
)
const AuthCallbackPage = React.lazy(() =>
  import('../pages/AuthCallbackPage').then((module) => ({
    default: module.AuthCallbackPage,
  }))
)
const NotFoundPage = React.lazy(() =>
  import('../pages/NotFoundPage').then((module) => ({
    default: module.NotFoundPage,
  }))
)
// Loading component for routes
const appShellRoutePrefixes = [
  '/boards',
  '/board',
  '/canvas',
  '/drafts',
  '/prints',
  '/account',
  '/support',
]

const RouteLoading: React.FC<{ pathname: string }> = ({ pathname }) => {
  const variant = appShellRoutePrefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
    ? 'app'
    : 'public'

  return <CalmShellPlaceholder variant={variant} />
}

const BoardsRouteEntry: React.FC = () => {
  const { isPhone } = useMobileDetection()

  if (isPhone) {
    return <MobileBoardsPage />
  }

  return <BoardsPage />
}

const NewBoardRouteEntry: React.FC = () => {
  return <NewBoardTemplatePage />
}

const RouterContent: React.FC = () => {
  const location = useLocation()

  return (
    <>
      <ScrollToTop />
      <GoogleAnalytics />
      <LazyChunkErrorBoundary>
        <Suspense fallback={<RouteLoading pathname={location.pathname} />}>
          <Routes>
            {/* Landing page */}
            <Route path="/" element={<LandingPage />} />

            {/* About page */}
            <Route path="/about" element={<AboutPage />} />

            {/* Sitemap */}
            <Route path="/sitemap" element={<SitemapPage />} />

            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/return-policy" element={<ReturnPolicyPage />} />
            <Route
              path="/support"
              element={
                <AuthGuard>
                  <ContactSupportPage />
                </AuthGuard>
              }
            />

            {/* Canonical board entrypoints */}
            <Route
              path="/board/new"
              element={
                <AuthGuard>
                  <NewBoardRouteEntry />
                </AuthGuard>
              }
            />

            {/* Canonical canvas editor route */}
            <Route
              path="/board/:boardId"
              element={
                <AuthGuard>
                  <CanvasGuard>
                    <LazyChunkErrorBoundary>
                      <CanvasApp />
                    </LazyChunkErrorBoundary>
                  </CanvasGuard>
                </AuthGuard>
              }
            />

            {/* Backward-compatible legacy /canvas URLs */}
            <Route
              path="/canvas/new"
              element={<Navigate to="/board/new" replace />}
            />
            <Route
              path="/canvas/:boardId"
              element={
                <AuthGuard>
                  <CanvasGuard>
                    <LazyChunkErrorBoundary>
                      <CanvasApp />
                    </LazyChunkErrorBoundary>
                  </CanvasGuard>
                </AuthGuard>
              }
            />
            <Route path="/canvas" element={<Navigate to="/boards" replace />} />

            {/* Admin Pages (unindexed) */}
            <Route path="/design" element={<DesignSystemPage />} />

            {/* Backward-compatible redirect for deprecated onboarding entrypoint */}
            <Route path="/start" element={<Navigate to="/boards" replace />} />

            {/* Protected pages */}
            <Route
              path="/boards"
              element={
                <AuthGuard>
                  <BoardsRouteEntry />
                </AuthGuard>
              }
            />
            <Route
              path="/drafts"
              element={
                <AuthGuard>
                  <DraftsPage />
                </AuthGuard>
              }
            />
            <Route
              path="/prints"
              element={
                <AuthGuard>
                  <OrdersPage />
                </AuthGuard>
              }
            />
            <Route
              path="/account"
              element={
                <AuthGuard>
                  <AccountPage />
                </AuthGuard>
              }
            />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* 404 page */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </LazyChunkErrorBoundary>
    </>
  )
}

export const AppRouter: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <RouterContent />
      </Router>
    </AuthProvider>
  )
}

export default AppRouter
